"use client";

import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/overlay";
import { ApiError, renovarSessao } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { applyTheme, usePrefs } from "./prefs";
import { useSession } from "./session";

let swStarted: Promise<void> | null = null;

/** Service worker do PWA (/sw.js): cache e página offline do app. */
function startServiceWorker() {
  if (!swStarted) {
    swStarted = (async () => {
      if (!("serviceWorker" in navigator)) return;
      await navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => undefined);
    })();
  }
  return swStarted;
}

function makeClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 1,
      },
    },
  });
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [client] = useState(makeClient);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    usePrefs.getState().hydrate();
    useSession.getState().hydrate();
    let alive = true;
    startServiceWorker()
      // Aba nova (sessionStorage vazio): recupera a sessão pelo cookie antes de decidir login × app
      .then(() => (useSession.getState().token ? undefined : renovarSessao().then(() => undefined)))
      .catch((e) => console.error("Falha ao iniciar mocks", e))
      .finally(() => {
        if (alive) setReady(true);
      });
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const { theme, density } = usePrefs.getState();
      if (theme === "system") applyTheme(theme, density);
    };
    mq.addEventListener("change", onChange);
    return () => {
      alive = false;
      mq.removeEventListener("change", onChange);
    };
  }, []);

  return (
    <QueryClientProvider client={client}>
      <TooltipProvider delayDuration={450} skipDelayDuration={150}>
        {ready ? (
          children
        ) : (
          <div className="flex h-full items-center justify-center text-muted">
            <Spinner className="size-4" />
          </div>
        )}
        <Toaster
          position="bottom-right"
          offset={{ bottom: 36, right: 16 }}
          visibleToasts={4}
          duration={3500}
          toastOptions={{
            unstyled: true,
            classNames: {
              toast:
                "flex w-[340px] items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg shadow-popover [&_[data-icon]]:shrink-0 [&_[data-icon]_svg]:size-4",
              success: "[&_[data-icon]]:text-success",
              error: "[&_[data-icon]]:text-danger",
              warning: "[&_[data-icon]]:text-warning",
              info: "[&_[data-icon]]:text-info",
              title: "truncate",
              actionButton: "ml-auto h-6 rounded-sm px-2 text-xs font-medium text-primary hover:bg-primary-soft",
            },
          }}
        />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
